import { PageOptions, PaginatedResponse } from '../api.types';
import { EmailParticipant, EmailParticipantType } from '../email-sync/email-sync.types';

export interface Comment {
  id: string;
  content: string;
  /**
   * The thread the note lives on. A merged notes feed spans several, so edit and delete must address
   * a comment through this rather than through whichever thread the list was opened for.
   */
  thread_id: string;
  /** Set only by the merged notes feed — which record this note was written against. */
  source?: NoteSource;
  type: CommentType;
  is_edited: boolean;
  data: ICommentData;
  /** JT1-1596: the note's configured type. Absent on chat, email and meeting comments. */
  note_type?: CommentNoteType;
  created_at: string;
  updated_at: string;
  account: CommentAccount;
}

export enum NoteSourceType {
  CANDIDATE = 'candidate',
  APPLICATION = 'application',
  // JT1-1736: a deal shares notes with the contacts and companies linked to it
  DEAL = 'deal',
  CONTACT = 'contact',
  COMPANY = 'company',
}

export interface NoteSource {
  type: NoteSourceType;
  application_id?: string;
  job_id?: string;
  job_title?: string;
  deal_id?: string;
  deal_title?: string;
  contact_id?: string;
  contact_name?: string;
  company_profile_id?: string;
  company_name?: string;
}

export interface CommentNoteType {
  id: string;
  name: string;
  icon: string;
  colour: string;
  system_key: string | null;
}

export interface ICommentData {
  text?: string;
  activity?: ICommentActivityType;
  email?: ICommentEmailType;
  meeting?: ICommentMeetingType;
  tagged_accounts?: CommentAccount[];
  /** JT1-1738: jobs linked from the note with `#`. */
  tagged_jobs?: CommentTaggedJob[];
}

export interface ICommentMeetingType {
  meeting_id: string;
  summary: string;
}

export interface ICommentActivityType {
  /**
   * JT1-1596: superseded by note_type_id when posting, and by Comment.note_type when reading. Still
   * written by the server for one release, so anything reading it keeps working.
   */
  type?: CommentActivityType;
  note_type_id?: string;
  text: string;
}

export interface ICommentEmailType {
  message_id: string;
  type: EmailParticipantType;
  content: string;
  from: EmailParticipant[];
  to: EmailParticipant[];
  cc?: EmailParticipant[];
  bcc?: EmailParticipant[];
  attachments?: {
    id: string;
    filename: string;
    mime_type: string;
    public_path: string;
  }[];
}

export enum CommentActivityType {
  GENERAL = 'general',
  PHONE_CALL = 'phone_call',
  CALL_RECEIVED = 'call_received',
  MEETING = 'meeting',
}

export enum CommentType {
  TEXT = 'text',
  ACTIVITY = 'activity',
  EMAIL = 'email',
  MEETING = 'meeting'
}

export enum ThreadType {
  CHAT = 'chat',
  NOTES = 'notes'
}

export interface CommentAccount {
  id: string;
  first_name: string;
  last_name: string;
  /** True for a guest-portal stakeholder — an external reviewer, not a member of the org. */
  is_guest?: boolean;
}

export interface CommentTaggedJob {
  id: string;
  title: string;
  /**
   * Set when the note is about a candidate with an application on this job: the tag opens the
   * application. Otherwise it opens the job.
   */
  application_id?: string | null;
}

export type GetThreadProps = {
  thread_id: string;
} & PageOptions;

export interface PostCommentProps {
  thread_id: string;
  type: CommentType;
  data: ICommentData;
}

export type GetCommentsResponse = PaginatedResponse<Comment>;

/**
 * Which record's merged notes feed to read. The server derives the thread set from this — the client
 * never names threads, because they are private per-organization and span records it cannot see.
 */
export type NotesFeedSource = {
  type: 'application' | 'candidate' | 'deal' | 'contact' | 'company';
  /** Record id matching `type` (a company feed takes the company profile id). */
  id: string;
  /** Candidate feeds only: narrow to one application's own notes. */
  application_id?: string;
};

export type GetNotesFeedProps = NotesFeedSource & PageOptions;

export interface IThread {
  id: string;
  type: ThreadType;
  created_at: string;
};

export enum ThreadVisibility {
  SHARED = 'shared',
  PRIVATE = 'private',
}

export type AttachedThread = {
  id: string;
  last_activity_at?: Date;
  /**
   * PRIVATE means only the managing organisation is on the thread. On the Employer Chat that is the
   * guest-notes case: the thread was created to hold a guest's notes on a job with no client
   * organisation, so a reply typed here reaches nobody (JT1-1491).
   */
  visibility?: ThreadVisibility;
};
